import { useState } from 'react';
import type { Greenhouse } from '../../services/greenhousesApi';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoreHorizontal, Search, Trash2, Droplet, Wind, Zap } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteGreenhouse } from '../../hooks/useGreenhouses';
import { EditGreenhouseDialog } from './EditGreenhouseDialog';
import { ViewGreenhouseDialog } from '../bays/ViewGreenhouseDialog';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';

interface GreenhousesTableProps {
  greenhouses: Greenhouse[];
}

export const GreenhousesTable = ({ greenhouses }: GreenhousesTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const deleteMutation = useDeleteGreenhouse();
  const confirm = useConfirm();

  const filteredGreenhouses = greenhouses.filter(
    (gh) =>
      gh.greenhouseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gh.greenhouseCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gh.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: 'Delete greenhouse?',
      description: `“${name}” will be permanently removed. This can't be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search greenhouses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Size (m²)</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Features</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGreenhouses.map((gh) => (
              <TableRow key={gh.id}>
                <TableCell className="font-medium">{gh.greenhouseName}</TableCell>
                <TableCell className="text-sm">{gh.greenhouseCode}</TableCell>
                <TableCell className="text-sm break-words max-w-xs">{gh.location}</TableCell>
                <TableCell className="text-sm">{gh.sizeSquareMeters}</TableCell>
                <TableCell className="text-sm">
                  <div>
                    <p className="font-medium">{gh.managerUserName}</p>
                    <p className="text-xs text-gray-500">{gh.managerEmail}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {gh.hasIrrigation && (
                      <Badge
                        variant="outline"
                        className="gap-1"
                        title="Irrigation"
                      >
                        <Droplet className="w-3 h-3" />
                      </Badge>
                    )}
                    {gh.hasClimateControl && (
                      <Badge variant="outline" className="gap-1" title="Climate Control">
                        <Wind className="w-3 h-3" />
                      </Badge>
                    )}
                    {gh.hasAutomation && (
                      <Badge variant="outline" className="gap-1" title="Automation">
                        <Zap className="w-3 h-3" />
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={gh.isActive ? 'default' : 'destructive'}
                  >
                    {gh.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <EditGreenhouseDialog greenhouse={gh} />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <ViewGreenhouseDialog greenhouse={gh} />
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(gh.id, gh.greenhouseName)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredGreenhouses.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No greenhouses found
        </div>
      )}
    </div>
  );
};